<template>
  <div class="container">
    <div class="box">
      <div class="is-flex is-flex-direction-column flex-gap-10">
        <div class="columns">
          <div class="column">
            <b-field label="From">
              <b-datetimepicker v-model="formProps.dateFrom" placeholder="Click to select..." :date-formatter="dateFormatter" editable>
                <template slot="left">
                  <button class="button is-primary" @click="formProps.dateFrom = new Date()">
                    <b-icon icon="clock"></b-icon>
                    <span>Now</span>
                  </button>
                </template>
                <template slot="right">
                  <button class="button is-danger" @click="formProps.dateFrom = null">
                    <b-icon icon="close"></b-icon>
                    <span>Clear</span>
                  </button>
                </template>
              </b-datetimepicker>
            </b-field>
          </div>
          <div class="column">
            <b-field label="To">
              <b-datetimepicker v-model="formProps.dateTo" placeholder="Click to select..." :date-formatter="dateFormatter" editable>
                <template slot="left">
                  <button class="button is-primary" @click="formProps.dateTo = new Date()">
                    <b-icon icon="clock"></b-icon>
                    <span>Now</span>
                  </button>
                </template>
                <template slot="right">
                  <button class="button is-danger" @click="formProps.dateTo = null">
                    <b-icon icon="close"></b-icon>
                    <span>Clear</span>
                  </button>
                </template>
              </b-datetimepicker>
            </b-field>
          </div>
        </div>
        <div class="level" v-if="hasIcabbiAccess">
          <div class="level-right">
            <b-button type="is-warning" class="is-halfwidth" @click="isFiltersOpen = !isFiltersOpen" :icon-right="isFiltersOpen ? 'chevron-up' : 'chevron-down'">Filters</b-button>
          </div>
        </div>
        <b-collapse v-model="isFiltersOpen" animation="slide" class="panel" v-if="hasIcabbiAccess">
          <div class="columns panel-block">
            <div class="column">
              <b-field label="Filter">
                <b-select placeholder="Filter" v-model="filter">
                  <option>No Filter</option>
                  <option>Customer</option>
                  <option>Region</option>
                  <option>Currency</option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Option" v-if="this.filter != 'No Filter'">
                <b-select v-if="this.filter == 'Customer'" label="Option" v-model="formProps.customerId">
                  <option v-for="customer in customers" :value="customer.disctools_id" :key="customer.disctools_id">{{ customer.customer_name }}</option>
                </b-select>
                <b-select v-if="this.filter == 'Region'" label="Option" v-model="formProps.regionId">
                  <option v-for="region in regions" :value="region.id" :key="region.id">{{ region.region_name }}</option>
                </b-select>
                <b-select v-if="this.filter == 'Currency'" label="Option" v-model="formProps.currency">
                  <option v-for="currency of customerCurrencies" :value="currency" :key="currency">{{ currency }}</option>
                </b-select>
              </b-field>
            </div>
          </div>
        </b-collapse>
        <div class="is-flex flex-gap-10">
          <b-button type="is-primary" @click="GetItems()">Submit</b-button>
          <b-button type="is-warning" v-if="hasIcabbiAccess" @click="isConfigVisible = true"><b-icon icon="cog" size="is-small"></b-icon></b-button>
          <template v-if="loaded">
            <download-csv class="button is-success" v-if="hasIcabbiAccess" :data="apiData" name="VoicePlusBilling.csv">Export All</download-csv>
          </template>
          <b-button type="is-danger" v-if="filter != 'No Filter'" @click="ClearFilters()">Clear Filters</b-button>
        </div>
      </div>
    </div>
    <template v-if="loaded">
      <template v-for="(reseller, key) in billingData">
        <div class="box" :key="key" v-if="UserHasAccess(key)">
          <div class="level">
            <div class="level-left">
              <div class="title is-size-5">{{ reseller.title }}</div>
            </div>
            <div class="level-right">
              <template v-if="loaded">
                <download-csv class="button is-success" :data="reseller.exportData" :name="key + ' - VoicePlusBilling.csv'">Export Section</download-csv>
              </template>
            </div>
          </div>
          <div class="level">
            <div class="level-item has-text-centered">
              <div>
                <p class="heading">Customers</p>
                <p class="title">{{ reseller.totals.customers }}</p>
              </div>
            </div>
            <div class="level-item has-text-centered">
              <div>
                <p class="heading">Calls</p>
                <p class="title">{{ reseller.totals.calls }}</p>
              </div>
            </div>
            <div class="level-item has-text-centered">
              <div>
                <p class="heading">Chargeable</p>
                <p class="title">{{ reseller.totals.chargeable }}</p>
              </div>
            </div>
            <div class="level-item has-text-centered">
              <div>
                <p class="heading">Fixed Location</p>
                <p class="title">{{ reseller.totals.fixed }}</p>
              </div>
            </div>
          </div>
          <b-table :data="reseller.data" :loading="isLoading" detailed hoverable detail-key="customerInfo" sticky-header>
            <b-table-column field="customerInfo.uuid" label="UID" sortable>
              <template v-slot:header="{ column }">
                <b-tooltip label="Provided by the finance team" dashed position="is-right">{{ column.label }}</b-tooltip>
              </template>
              <template v-slot="props">
                {{ props.row.customerInfo.uuid }}
              </template>
            </b-table-column>
            <b-table-column field="customerInfo.customer_name" label="Customer" v-slot="props" sortable>{{ props.row.customerInfo.customer_name }} </b-table-column>
            <b-table-column field="TotalCalls" label="Cost" sortable>
              <template v-slot:header="{ column }">
                <b-tooltip label="Total cost for the speech to text and dialogflow requests" dashed position="is-bottom">{{ column.label }}</b-tooltip>
              </template>
              <template v-slot="props"> {{ CurrencyFormatter(CalculateCost(props.row), props.row.customerInfo.currency) }}</template>
            </b-table-column>
            <b-table-column field="TotalCalls" label="Calls" sortable>
              <template v-slot:header="{ column }">
                <b-tooltip label="Calls which have passed through Voice+" dashed position="is-bottom">{{ column.label }}</b-tooltip>
              </template>
              <template v-slot="props">
                {{ props.row.TotalCalls }}
              </template>
            </b-table-column>
            <b-table-column field="ChargableCalls" label="Chargable Calls" sortable>
              <template v-slot:header="{ column }">
                <b-tooltip label="Calls which have passed through Voice+ and have chargable outcomes" dashed position="is-bottom">{{ column.label }}</b-tooltip>
              </template>
              <template v-slot="props">
                {{ props.row.ChargableCalls }}
              </template>
            </b-table-column>
            <b-table-column field="FixedLocationCalls" label="F/L calls" sortable>
              <template v-slot:header="{ column }">
                <b-tooltip label="Calls from fixed locations" dashed position="is-bottom">{{ column.label }}</b-tooltip>
              </template>
              <template v-slot="props">
                {{ props.row.FixedLocationCalls }}
              </template>
            </b-table-column>
            <!--
          <b-table-column field="FixedLocationCalls" label="Total Charge" sortable>
            <template v-slot:header="{ column }">
              <b-tooltip label="Chargable calls multiplied by customer's call cost" dashed position="is-bottom">{{ column.label }}</b-tooltip>
            </template>
            <template v-slot="props">
              {{
                CurrencyFormatter(
                  CalculateTotal(props.row.ChargableCalls, props.row.customerInfo.call_cost, props.row.currency) +
                    CalculateTotal(props.row.FixedLocationCalls, props.row.customerInfo.fixed_loc_cost, props.row.customerInfo.currency),
                  props.row.customerInfo.currency
                )
              }}
            </template>
          </b-table-column>
          <b-table-column field="ChargableCalls" label="Profit/Loss" sortable>
            <template v-slot:header="{ column }">
              <b-tooltip label="Shows the amount of profit (or loss) made" dashed position="is-left">{{ column.label }}</b-tooltip>
            </template>
            <template v-slot="props">
              {{
                CurrencyFormatter(
                  CalculateTotal(props.row.ChargableCalls, props.row.customerInfo.call_cost, props.row.customerInfo.currency) +
                    CalculateTotal(props.row.FixedLocationCalls, props.row.customerInfo.fixed_loc_cost, props.row.customerInfo.currency) -
                    CalculateCost(props.row),
                  props.row.customerInfo.currency
                )
              }}
            </template>
          </b-table-column>
          -->
            <template slot="detail" slot-scope="props">
              <div class="columns">
                <table class="column">
                  <tr class="block" v-if="props.row.SpeechToText">
                    <td class="name">Speech to text seconds:</td>
                    <td class="value">
                      {{ props.row.SpeechToText }}
                    </td>
                    <td class="cost">{{ CurrencyFormatter(CalculateSTT(props.row.SpeechToText, props.row.customerInfo.currency), props.row.customerInfo.currency) }}</td>
                  </tr>
                  <tr class="block" v-if="props.row.EnhancedSpeechToText">
                    <td class="name">Enhanced Speech to text seconds:</td>
                    <td class="value">
                      {{ props.row.EnhancedSpeechToText }}
                    </td>
                    <td class="cost">{{ CurrencyFormatter(CalculateSTT(props.row.EnhancedSpeechToText, true), props.row.customerInfo.currency) }}</td>
                  </tr>
                  <tr class="block">
                    <td class="name">Speech to text requests:</td>
                    <td class="value">
                      {{ props.row.TotalSpeechToTextRequests }}
                    </td>
                    <td class="cost">N/A</td>
                  </tr>
                  <tr class="block">
                    <td class="name">Dialogflow Requests:</td>
                    <td class="value">
                      {{ props.row.DialogFlow }}
                    </td>
                    <td class="cost">{{ CurrencyFormatter(CalculateDialogflow(props.row.DialogFlow), props.row.customerInfo.currency) }}</td>
                  </tr>
                  <tr class="block" v-for="item in props.row.LookupStats.LookupRequests" :key="item.ProviderName + item.ProviderType">
                    <td class="name">{{ item.ProviderName }} {{ item.ProviderType }}:</td>
                    <td class="value">
                      {{ item.Count }}
                    </td>
                    <td>{{ CurrencyFormatter(CalculateLookupStats(item.ProviderName, item.ProviderType, item.Count, props.row.customerInfo.currency), props.row.customerInfo.currency) }}</td>
                  </tr>
                </table>

                <table class="column">
                  <tr class="block">
                    <td class="name">Call Cost:</td>
                    <td class="value">{{ props.row.customerInfo.call_cost }} {{ props.row.customerInfo.currency }}</td>
                  </tr>
                  <tr class="block">
                    <td class="name">Fixed Location Call Cost:</td>
                    <td class="value">{{ props.row.fixed_loc_cost }} {{ props.row.customerInfo.currency }}</td>
                  </tr>
                  <tr class="block">
                    <td class="name">Current conversion rate USD - GBP:</td>
                    <td class="value">
                      {{ currencies["USD"] }}
                    </td>
                  </tr>
                  <tr class="block">
                    <td class="name">Current conversion rate GBP - {{ props.row.customerInfo.currency }}:</td>
                    <td class="value">
                      {{ currencies[props.row.customerInfo.currency] }}
                    </td>
                  </tr>
                </table>
              </div>
            </template>
          </b-table>
        </div>
      </template>
    </template>
    <template v-if="isLoading">
      <div class="box">
        <div class="is-flex is-flex-direction-column is-align-items-center">
          <loading-spinner></loading-spinner>
          <div class="title is-size-5">Generating Report</div>
          <div class="subtitle is-size-6 has-text-grey">This may take a while based on specified parameters</div>
          <div class="is-size-7 has-text-grey-light has-text-weight-medium">Completed {{ this.completedSearch }} / {{ this.totalSearch }}</div>
        </div>
      </div>
    </template>
    <b-modal :active.sync="isConfigVisible" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <billing-config @submitted="UpdateConfig" :item="this.config" :tags="this.tags"></billing-config>
    </b-modal>
  </div>
</template>

<style scoped>
.tile.is-parent {
  gap: 15px;
}
.content {
  width: 100%;
}
.container {
  margin: 20px auto 20px auto;
}
.buttonWrap {
  grid-column: 1/3;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 20px;
}
</style>
<style>
span.select {
  width: 100%;
}
select {
  width: 100%;
}
</style>

<script>
import LoadingSpinner from "../../../components/common/LoadingSpinner.vue";
const axios = require("axios");
const downloadCsv = require("vue-json-csv");
const dayjs = require("dayjs");
const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
const customParseFormat = require("dayjs/plugin/customParseFormat");
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
const billingConfig = {
  props: {
    item: {
      type: Object,
      required: true,
    },
    tags: {
      type: Array,
      required: true,
    },
  },
  data: function () {
    return {};
  },
  methods: {},
  template: `
        <form @submit.prevent="">
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">Billing Configuration</p>
                </header>
                <section class="modal-card-body">
                    <b-field label="Speech to text fomula">
                        <b-input v-model="item.speech_to_text_formula">
                        </b-input>
                    </b-field>
                    <b-field label="Enhanced speech to text fomula">
                        <b-input v-model="item.enhanced_speech_to_text_formula">
                        </b-input>
                    </b-field>
                    <b-field label="Dialogflow formula">
                        <b-input v-model="item.dialogflow_formula">
                        </b-input>
                    </b-field>
                    <b-field label="Google Autocomplete formula">
                        <b-input v-model="item.autocomplete_formula">
                        </b-input>
                    </b-field>
                    <b-field label="Google Find Places formula">
                        <b-input v-model="item.places_formula">
                        </b-input>
                    </b-field>
                    <b-field label="Google Geocoding formula">
                        <b-input v-model="item.geocode_formula">
                        </b-input>
                    </b-field>
                    <b-field label="Chargable Tags">
                      <b-select multiple v-model="item.tags">
                        <option v-for="tag in tags">{{tag.name}}</option>
                      </b-select>
                    </b-field>
                </section>
                <footer class="modal-card-foot">
                    <button class="button" type="button" @click="$parent.close()">Cancel</button>
                    <button class="button is-primary" @click="$emit('submitted')">Update</button>
                </footer>
            </div>
        </form>
        `,
};
export default {
  components: {
    billingConfig,
    downloadCsv,
    LoadingSpinner,
  },
  data: function () {
    return {
      newApiUrl: "https://api.jeak.co.uk",
      exchangeRateApiKey: process.env.VUE_APP_EXCHANGE_RATE_API_KEY || "",
      dateoptions: { year: "numeric", month: "short", day: "numberic" },
      formProps: {
        dateFrom: new Date(new Date().setHours(0, 0, 0, 0)),
        dateTo: new Date(new Date().setHours(23, 59, 59)),
      },
      customers: [],
      apiData: [],
      billingData: {
        etgl: { title: "Exel Technology Group", data: [], exportData: [], totals: { customers: 0, calls: 0, chargeable: 0, fixed: 0 } },
        icabbi: { title: "iCabbi", data: [], exportData: [], totals: { customers: 0, calls: 0, chargeable: 0, fixed: 0 } },
      },
      requests: 0,
      isConfigVisible: false,
      config: {},
      filter: "No Filter",
      regions: [],
      isLoading: false,
      currencies: [],
      customerCurrencies: [],
      tags: [],
      loaded: false,
      currency_symbols: {
        USD: "$", // US Dollar
        EUR: "€", // Euro
        CRC: "₡", // Costa Rican Colón
        GBP: "£", // British Pound Sterling
        ILS: "₪", // Israeli New Sheqel
        INR: "₹", // Indian Rupee
        JPY: "¥", // Japanese Yen
        KRW: "₩", // South Korean Won
        NGN: "₦", // Nigerian Naira
        PHP: "₱", // Philippine Peso
        PLN: "zł", // Polish Zloty
        PYG: "₲", // Paraguayan Guarani
        THB: "฿", // Thai Baht
        UAH: "₴", // Ukrainian Hryvnia
        VND: "₫", // Vietnamese Dong
        CAD: "$", // Canadian Dollar
        AUD: "$", // Australian Dollar
      },
      allCustomers: [],
      isFiltersOpen: false,
      totalSearch: 0,
      completedSearch: 0,
      requestSequence: 0,
    };
  },
  methods: {
    dateFormatter(dt) {
      return dayjs(dt).format();
    },
    CalculateCost: function (data) {
      if (!data.customerInfo.currency || data.customerInfo.currency == null) data.customerInfo.currency = "GBP";
      let cost = 0;
      if (data.SpeechToText) {
        cost += this.CalculateSTT(data.SpeechToText, true);
      }
      if (data.EnhancedSpeechToText) {
        cost += this.CalculateSTT(data.EnhancedSpeechToText, true);
      }
      if (data.DialogFlow) {
        cost += this.CalculateDialogflow(data.DialogFlow);
      }
      if (typeof data.LookupStats !== "undefined" && typeof data.LookupStats.LookupRequests !== "undefined") {
        data.LookupStats.LookupRequests.forEach((stat) => {
          cost += this.CalculateLookupStats(stat.ProviderName, stat.ProviderType, stat.Count);
        });
      }
      return cost;
    },
    CalculateSTT: function (speechToTextAmount, enhanced = false) {
      let cost = 0;
      if (speechToTextAmount && enhanced) {
        cost += eval(`${speechToTextAmount.toString()}${this.config.enhanced_speech_to_text_formula}`);
      } else {
        cost += eval(`${speechToTextAmount.toString()}${this.config.speech_to_text_formula}`);
      }
      return cost;
    },
    CalculateDialogflow: function (dialogFlowAmount) {
      let cost = 0;
      if (dialogFlowAmount) {
        cost += eval(`${dialogFlowAmount.toString()}${this.config.dialogflow_formula}`);
      }
      return cost;
    },
    CalculateLookupStats: function (ProviderName, ProviderType, Count) {
      let cost = 0;
      if (ProviderName == "google" && ProviderType == "autocomplete") {
        cost += eval(`${Count.toString()}${this.config.autocomplete_formula}`);
      } else if (ProviderName == "google" && ProviderType == "placesfromtext") {
        cost += eval(`${Count.toString()}${this.config.places_formula}`);
      } else if (ProviderName == "google" && ProviderType == "geocode") {
        cost += eval(`${Count.toString()}${this.config.geocode_formula}`);
      }
      return cost;
    },
    CalculateTotal: function (amount, callCost, currency = "GBP") {
      //convert cost to gbp
      callCost = callCost / this.currencies[currency];
      //calculate total
      amount = amount * callCost;
      //convert back into customers currency
      amount = amount * this.currencies[currency];
      return amount;
    },
    ConvertToGBP: function (amount, currency) {
      amount = amount / this.currencies[currency];
      amount = amount.toFixed(2);
      return amount;
    },
    ConvertToMinutes: function (amount) {
      let time = "";
      let minutes = Math.floor(amount / 60);
      let seconds = amount - minutes * 60;
      time = `${minutes}:${seconds}`;
      return time;
    },
    CurrencyFormatter(amount, customerCurrency, fromCurrency = "USD") {
      let currency = "";
      let amountInPounds = this.ConvertToGBP(amount, fromCurrency);
      let amountInCustomerCurrency = amountInPounds * this.currencies[customerCurrency];
      amount = amountInCustomerCurrency.toFixed(2);
      currency = `${this.currency_symbols[customerCurrency]}${amount}`;
      return currency;
    },
    GetItems: function () {
      const requestId = ++this.requestSequence;
      this.isLoading = true;
      this.loaded = false;
      this.requests = 0;
      this.totalSearch = 0;
      this.completedSearch = 0;
      let from = this.dateFormatter(this.formProps.dateFrom);
      let to = this.dateFormatter(this.formProps.dateTo);
      this.billingData = {
        etgl: { title: "Exel Technology Group", data: [], exportData: [], totals: { customers: 0, calls: 0, chargeable: 0, fixed: 0 } },
        icabbi: { title: "iCabbi", data: [], exportData: [], totals: { customers: 0, calls: 0, chargeable: 0, fixed: 0 } },
      };

      this.isLoading = true;
      this.loaded = false;

      let promiseList = [];
      if (this.filter == "No Filter") {
        for (const customer of this.customerList) {
          if (customer.disctools_id != 0) {
            promiseList.push(this.GetBillingDataForCustomer(customer.disctools_id, from, to, requestId));
            this.totalSearch += 1;
          }
        }
      } else {
        if (this.formProps.customerId) {
          this.totalSearch += 1;
          promiseList.push(this.GetBillingDataForCustomer(this.formProps.customerId, from, to, requestId));
        } else if (this.formProps.currency) {
          this.customers.forEach((customer) => {
            if (customer.currency == this.formProps.currency) {
              this.totalSearch += 1;
              promiseList.push(this.GetBillingDataForCustomer(customer.disctools_id, from, to, requestId));
            }
          });
        } else if (this.formProps.regionId) {
          this.customers.forEach((customer) => {
            if (customer.region_id == this.formProps.regionId) {
              this.totalSearch += 1;
              promiseList.push(this.GetBillingDataForCustomer(customer.disctools_id, from, to, requestId));
            }
          });
        }
      }
      Promise.all(promiseList)
        .then(() => {
          if (requestId !== this.requestSequence) return;
          this.isLoading = false;
          this.FormatBillingData();
          this.loaded = true;
        })
        .catch(() => {
          if (requestId !== this.requestSequence) return;
          this.isLoading = false;
          this.FormatBillingData();
          this.loaded = true;
        });
    },
    GetBillingDataForCustomer: async function (customerId, from, to, requestId = null) {
      const customerDetails = this.customers.find((customer) => {
        return customer.disctools_id == customerId;
      });
      if (!customerDetails || !customerDetails.timezone) {
        this.completedSearch += 1;
        return;
      }

      from = dayjs(from).tz(customerDetails.timezone, true);
      to = dayjs(to).tz(customerDetails.timezone, true);
      let formattedFrom = from.utc().format();
      let formattedTo = to.utc().format();

      const token = await this.$cognitoAuth.getIdToken();
      const config = {
        headers: { Authorization: "Bearer " + token },
      };
      if (customerDetails.type && customerDetails.type.toLowerCase() == "dev") {
        this.completedSearch += 1;
        return;
      }
      const response = await axios.get(`${this.newApiUrl}/billing?dateFrom=${formattedFrom}&dateTo=${formattedTo}&customerId=${customerId}`, config);
      if (requestId !== null && requestId !== this.requestSequence) return;

      const tmpObj = response.data;
      tmpObj.customerInfo = customerDetails;
      let customerDiscTools = this.allCustomers.find((customer) => {
        return customer.id == customerId;
      });
      if (!customerDiscTools) {
        this.completedSearch += 1;
        return;
      }
      let reference = {};
      if (customerDiscTools.IsETGLCustomer == 1) {
        reference = this.billingData["etgl"];
      } else {
        reference = this.billingData["icabbi"];
      }
      reference.data.push(tmpObj);
      reference.totals.customers += 1;
      this.completedSearch += 1;
      reference.totals.calls += tmpObj.TotalCalls;
      reference.totals.chargeable += tmpObj.ChargableCalls;
      reference.totals.fixed += tmpObj.FixedLocationCalls;

      /*
        catch((error) => {
          this.requests -= 1;
          if (this.requests == 0) {
            this.isLoading = false;
            this.loaded = true;
          }
          console.log(error);
          this.$buefy.notification.open({
            message: `Could not get billing information for ${
              this.customers.filter((customer) => {
                return customer.disctools_id == customerId;
              })[0].customer_name
            }\n\nPlease contact Support for help`,
            position: "is-bottom-right",
            type: "is-danger",
            hasIcon: true,
            duration: 100000,
          });
        });
        */
    },
    GetCustomers: async function () {
      const token = await this.$cognitoAuth.getIdToken();
      const config = {
        headers: { Authorization: "Bearer " + token },
      };
      axios.get(this.newApiUrl + "/customers", config).then((response) => {
        this.customers = [{ disctools_id: 0, customer_name: "All Customers" }];
        response.data.forEach((value) => {
          if (value.type == null) {
            this.customers.push(value);
            if (!this.customerCurrencies.includes(value.currency)) {
              this.customerCurrencies.push(value.currency);
            }
          }
        });
        this.customers.sort((a, b) => {
          let name1 = a.customer_name;
          let name2 = b.customer_name;
          if (name1 < name2) {
            //name 1 is first alphabetically
            return -1;
          }
          if (name1 > name2) {
            // name2 comes first
            return 1;
          }
          return 0;
        });
      });
    },
    GetConfig: async function () {
      const token = await this.$cognitoAuth.getIdToken();
      const config = {
        headers: { Authorization: "Bearer " + token },
      };
      axios.get(this.newApiUrl + "/billing/config", config).then((response) => {
        this.config = response.data;
        this.config.tags = JSON.parse(this.config.tags);
      });
    },
    UpdateConfig: async function () {
      const token = await this.$cognitoAuth.getIdToken();
      const config = {
        headers: { Authorization: `Bearer ` + token },
      };
      axios
        .put(this.newApiUrl + `/billing/config`, this.config, config)
        .then(() => {
          this.$buefy.toast.open({
            message: "Billing Configuration updated",
            type: "is-success",
          });
          this.GetConfig();
          this.GetItems();
          this.isConfigVisible = false;
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: "An error occurred! If this persists, please contact support",
            type: "is-danger",
          });
        });
    },
    GetCurrencies: async function () {
      if (!this.exchangeRateApiKey) {
        this.currencies = {};
        return;
      }
      const response = await axios.get(`https://v6.exchangerate-api.com/v6/${this.exchangeRateApiKey}/latest/GBP`);
      this.currencies = response.data.conversion_rates;
      this.currencies = Object.keys(this.currencies)
        .sort()
        .reduce((result, key) => {
          result[key] = this.currencies[key];
          return result;
        }, {});
    },
    GetRegions: async function () {
      const token = await this.$cognitoAuth.getIdToken();
      const config = {
        headers: { Authorization: "Bearer " + token },
      };
      axios.get(this.newApiUrl + "/regions", config).then((response) => {
        let bufferedthis = this;
        response.data.forEach(function (value) {
          bufferedthis.regions.push(value);
        });
      });
    },
    GetTags: async function () {
      const token = await this.$cognitoAuth.getIdToken();
      const config = {
        headers: { Authorization: "Bearer " + token },
      };
      axios.get(this.newApiUrl + "/billing/tags", config).then((response) => {
        this.tags = response.data;
      });
    },
    FormatBillingData: async function () {
      this.apiData = [];
      this.billingData["etgl"].data.forEach((customer) => {
        const customerObj = {};
        customerObj.UID = customer.customerInfo.uuid;
        customerObj.Name = customer.customerInfo.customer_name;
        customerObj.USDCost = this.CalculateCost(customer, customer.customerInfo.currency).toFixed(2);
        customerObj.AddedDate = dayjs(customer.customerInfo.added).format("DD-MM-YYYY");
        customerObj.ContractEnd = dayjs(customer.customerInfo.trial_end).format("DD-MM-YYYY");
        customerObj.Cost = this.CurrencyFormatter(this.CalculateCost(customer), customer.customerInfo.currency);
        customerObj.Calls = customer.TotalCalls;
        customerObj.ChargableCalls = customer.ChargableCalls;
        customerObj.FixedLocationCalls = customer.FixedLocationCalls;
        customerObj.SpeechToTextSeconds = customer.SpeechToText;
        customerObj.EnhancedSpeechToTextSeconds = customer.EnhancedSpeechToText;
        customerObj.Autocomplete = customer.LookupStats.LookupRequests.find((stat) => stat.ProviderName == "google" && stat.ProviderType == "autocomplete").Count;
        customerObj.PlacesFromText = customer.LookupStats.LookupRequests.find((stat) => stat.ProviderName == "google" && stat.ProviderType == "placesfromtext").Count;
        customerObj.Geocode = customer.LookupStats.LookupRequests.find((stat) => stat.ProviderName == "google" && stat.ProviderType == "geocode").Count;
        customerObj.Charge = this.CurrencyFormatter(
          this.CalculateTotal(customer.ChargableCalls, customer.customerInfo.call_cost, customer.customerInfo.currency) +
            this.CalculateTotal(customer.FixedLocationCalls, customer.customerInfo.fixed_loc_cost, customer.customerInfo.currency),
          customer.customerInfo.currency
        );

        this.apiData.push(customerObj);
        this.billingData["etgl"].exportData.push(customerObj);
      });

      this.billingData["icabbi"].data.forEach((customer) => {
        const customerObj = {};
        customerObj.UID = customer.customerInfo.uuid;
        customerObj.Name = customer.customerInfo.customer_name;
        customerObj.USDCost = this.CalculateCost(customer, customer.customerInfo.currency).toFixed(2);
        customerObj.AddedDate = dayjs(customer.customerInfo.added).format("DD-MM-YYYY");
        customerObj.ContractEnd = dayjs(customer.customerInfo.trial_end).format("DD-MM-YYYY");
        customerObj.Cost = this.CurrencyFormatter(this.CalculateCost(customer), customer.customerInfo.currency);
        customerObj.Calls = customer.TotalCalls;
        customerObj.ChargableCalls = customer.ChargableCalls;
        customerObj.FixedLocationCalls = customer.FixedLocationCalls;
        customerObj.SpeechToTextSeconds = customer.SpeechToText;
        customerObj.EnhancedSpeechToTextSeconds = customer.EnhancedSpeechToText;
        customerObj.Autocomplete = customer.LookupStats.LookupRequests.find((stat) => stat.ProviderName == "google" && stat.ProviderType == "autocomplete").Count;
        customerObj.PlacesFromText = customer.LookupStats.LookupRequests.find((stat) => stat.ProviderName == "google" && stat.ProviderType == "placesfromtext").Count;
        customerObj.Geocode = customer.LookupStats.LookupRequests.find((stat) => stat.ProviderName == "google" && stat.ProviderType == "geocode").Count;
        customerObj.Charge = this.CurrencyFormatter(
          this.CalculateTotal(customer.ChargableCalls, customer.customerInfo.call_cost, customer.customerInfo.currency) +
            this.CalculateTotal(customer.FixedLocationCalls, customer.customerInfo.fixed_loc_cost, customer.customerInfo.currency),
          customer.customerInfo.currency
        );

        this.apiData.push(customerObj);
        this.billingData["icabbi"].exportData.push(customerObj);
      });
    },
    Load: function () {
      this.GetCustomers();
      this.GetConfig();
      this.GetCurrencies();
      this.GetRegions();
      this.GetTags();
      this.GetAllCustomers();
    },
    ClearFilters: function () {
      this.filter = "No Filter";
      this.formProps.customerId = 0;
      this.formProps.currency = 0;
      this.formProps.regionId = 0;
    },
    GetAllCustomers: async function () {
      const token = await this.$cognitoAuth.getIdToken();
      const config = {
        headers: { Authorization: "Bearer " + token },
      };
      const response = await axios.get(`https://api.disctools.co.uk/api/customers`, config);
      this.allCustomers = response.data.data;
    },
    UserHasAccess: function (sectionName) {
      for (const group of this.lowercaseGroups) {
        if (group == "icabbi") return true;
        if (sectionName.toLowerCase() == group) {
          return true;
        }
      }
      return false;
    },
  },
  mounted() {
    this.Load();
  },
  computed: {
    groups: function () {
      if (this.$route && this.$route.params && Array.isArray(this.$route.params.groups)) {
        return this.$route.params.groups;
      }
      return [];
    },
    lowercaseGroups: function () {
      return this.groups.map((group) => String(group).toLowerCase());
    },
    hasIcabbiAccess: function () {
      return this.lowercaseGroups.includes("icabbi");
    },
    customerList: function () {
      let customerList = [];
      for (const i in this.customers) {
        let foundRecord = this.allCustomers.find((customerSearch) => {
          if (this.lowercaseGroups.includes("etgl") && customerSearch.IsETGLCustomer == 1) {
            return this.customers[i].disctools_id == customerSearch.id;
          } else if (this.lowercaseGroups.includes("icabbi")) {
            return this.customers[i].disctools_id == customerSearch.id;
          }
          return false;
        });
        if (foundRecord) {
          customerList.push(this.customers[i]);
        }
      }
      return customerList;
    },
  },
};
</script>
